import { Fragment, useEffect } from "react";
import { PaxErrorPage, PaxTab, PaxTabPanel, useTranslation } from "@ncino/pax-ui";
import { PoolLoiStatus, useGetPoolForSellPageQuery } from "@/services/graphql/reactQuery";
import { z } from "zod";
import { PageContentContainer } from "@/shared/components/PageContentContainer";
import { Box, Stack, Typography, Tabs, Divider } from "@mui/material";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { makeDateFormatter } from "../manage/-internal/ValidationStep/types";
import { LoadingPageComponent } from "@/shared/components/LoadingPage";
import { TearSheet } from "@/shared/components/PoolDetails/TearSheet";
import { useEffectivePortfolioId } from "@/shared/hooks/useActivePortfolio";
import { PoolAnalytics } from "@/shared/components/PoolDetails/PoolAnalytics";
import { PoolLoanList } from "@/shared/components/PoolDetails/PoolLoanList";
import { useSellRouteContext } from "./-internal/SellRouteContext";
import { PoolStatus, StatusBadge } from "./-internal/sellTableUtil";
import { SellerDueDiligenceTab } from "./-internal/SellerDueDiligenceTab";

const tabSchema = z.enum(["tearsheet", "analytics", "loan-list", "due-diligence"]);
type TabValue = z.infer<typeof tabSchema>;

export const Route = createFileRoute("/sell/$poolId")({
  component: RouteComponent,
  validateSearch: z.object({
    tab: tabSchema.catch("tearsheet"),
  }),
});

function RouteComponent() {
  const { t, i18n } = useTranslation("sell");
  const locale = i18n.language.replace("_", "-");

  const { poolId } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const { setActive } = useSellRouteContext();

  const handleNavigateToSell = () => {
    setActive(undefined);
    navigate({ to: "/sell" });
  };

  const handleTabChange = (_: React.SyntheticEvent, value: TabValue) => {
    navigate({
      from: Route.fullPath,
      search: { tab: value },
    });
  };

  const { portfolioId, institutionId, activeDataDate: dataDate } = useEffectivePortfolioId();
  const { data: poolData, isPending } = useGetPoolForSellPageQuery({ id: Number(poolId) });
  const { id, createdDate, poolName, loanCount, listing, loans } = poolData?.pool || {};
  const { servicingFeePercentage, poolType, grossYield } = poolData?.pool || {};
  const {
    carveOutPermitted,
    dueDiligencePeriod,
    dueDiligencePeriodDays,
    listingPercentage,
    sellerRetentionPercent,
    poolBalancePreSettlement,
    recourseType,
    servicingType,
    settlementType,
    listingStage,
    loiStatus,
  } = listing || {};

  // Falls back to the client-side sum only before a listing exists to read the server-computed
  // value from - once it does, poolBalancePreSettlement is refreshed at every trigger point and
  // is the authoritative value (the client-side sum includes charged-off/removed loans).
  const clientSideBalanceSum = loans?.reduce((sum, loan) => sum + (loan.currentBalance ?? 0), 0);
  const poolBalance = poolBalancePreSettlement ?? clientSideBalanceSum;
  // Falls back to 10, not 0, to mirror the backend's actual default for a missing value.
  const availableBalance = poolBalance
    ? poolBalance * (1 - (sellerRetentionPercent ?? 10) / 100)
    : null;

  const formattedDate =
    isPending || !createdDate
      ? ""
      : makeDateFormatter(locale).format(new Date(createdDate).getTime());

  const status = loiStatus || listingStage || "";
  const showTearSheet = status !== PoolStatus.draft && status !== PoolStatus.readyToPublish;

  useEffect(() => {
    if (tab === "tearsheet" && !showTearSheet) {
      navigate({ from: Route.fullPath, search: { tab: "analytics" } });
    } else if (tab === "analytics" && showTearSheet)
      navigate({ from: Route.fullPath, search: { tab: "tearsheet" } });
  }, [showTearSheet]);

  return (
    <PageContentContainer>
      {isPending ? (
        <LoadingPageComponent />
      ) : !id ? (
        <PaxErrorPage
          header={t("sellPage.poolTabDetails.poolNotFound")}
          subtext=""
          actionLabel={t("sellPage.poolTabDetails.returnToList")}
          onAction={handleNavigateToSell}
        />
      ) : (
        <Fragment>
          <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h6">{poolName}</Typography>
              <Typography variant="body1">
                {t("sellPage.poolTabDetails.created")} {formattedDate}
              </Typography>
            </Box>
            <StatusBadge status={status} sx={{ marginLeft: "24px" }} />
          </Stack>

          <Tabs
            value={tab}
            onChange={handleTabChange}
            aria-label="test"
            sx={{ "& .MuiTab-root": { textTransform: "none" } }}
          >
            {showTearSheet && (
              <PaxTab label={t("sellPage.poolTabDetails.tearSheet")} value="tearsheet" />
            )}
            <PaxTab label={t("sellPage.poolTabDetails.analytics")} value="analytics" />
            <PaxTab
              label={t("sellPage.poolTabDetails.loanList", { count: loanCount })}
              value="loan-list"
            />
            <PaxTab label={t("sellPage.poolTabDetails.dueDiligence")} value="due-diligence" />
          </Tabs>
          <Divider sx={{ mb: 4 }} />
          {showTearSheet && (
            <PaxTabPanel value={tab} index="tearsheet">
              <TearSheet
                poolBalance={poolBalance || null}
                totalLoans={loanCount || null}
                amountAvailable={availableBalance}
                grossYield={grossYield || null}
                assetClass={poolType || null}
                settlementType={settlementType || null}
                recourseType={recourseType || null}
                price={listingPercentage || null}
                diligencePeriodType={dueDiligencePeriod || null}
                diligenceDuration={dueDiligencePeriodDays}
                carveOut={carveOutPermitted || null}
                servicingType={servicingType || null}
                servicingFee={servicingFeePercentage || null}
              />
            </PaxTabPanel>
          )}
          <PaxTabPanel value={tab} index="analytics">
            {id && portfolioId && institutionId && dataDate && loans && (
              <PoolAnalytics
                dataDate={dataDate}
                poolId={id}
                portfolioId={portfolioId}
                institutionId={institutionId}
              />
            )}
          </PaxTabPanel>
          <PaxTabPanel value={tab} index="loan-list">
            <Typography>
              {loanCount && institutionId && (
                <PoolLoanList
                  poolId={id}
                  totalLoans={loanCount}
                  institutionId={institutionId}
                  isSellPage={true}
                />
              )}
            </Typography>
          </PaxTabPanel>
          <PaxTabPanel value={tab} index="due-diligence">
            <SellerDueDiligenceTab
              poolId={poolId}
              isUnderLoi={loiStatus === PoolLoiStatus.UnderLoi}
            />
          </PaxTabPanel>
        </Fragment>
      )}
    </PageContentContainer>
  );
}
